import moment from 'moment';
import React, { useState } from 'react';

import ApplicationDetailsInterface from '@api/requests/applications/interfaces/ApplicationDetailsInterface.interface';
import CustodianAgreementInterface from '@api/requests/applications/interfaces/CustodianAgreementInterface.interface';

import VisibilityIcon from '@mui/icons-material/Visibility';
import { Alert, Box, Divider, Grid, IconButton, Stack, Tooltip, Typography } from '@mui/material';

import { StyledDivider } from '@pages/Applications/styles';

import CustodianAgreementModal from './CustodianAgreementModal';
import { CUSTODIAN_AGREEMENTS_TEXTS } from './constants';
import useCustodianAgreements from './hooks';
import {
	getAgreementMandatorityLabel,
	getAgreementStatusIcon,
	getAgreementStatusLabel,
	getUnsignedRequiredAgreements,
} from './utils';

function CustodianAgreements({ applicationDetails }: { applicationDetails: ApplicationDetailsInterface }) {
	const agreements = applicationDetails.custodian_agreements;

	const [selectedAgreement, setSelectedAgreement] = useState<CustodianAgreementInterface | null>(null);
	const { handleAgree, agreeingId } = useCustodianAgreements(applicationDetails.id);

	if (!agreements || agreements.length === 0) return null;

	const unsignedRequiredAgreements = getUnsignedRequiredAgreements(agreements);
	const selectedFromStore = selectedAgreement
		? agreements.find(agreement => agreement.id === selectedAgreement.id) ?? null
		: null;

	const handleOpenAgreement = (agreement: CustodianAgreementInterface) => {
		setSelectedAgreement(agreement);
	};

	const handleAgreeAgreement = async (agreement: CustodianAgreementInterface) => {
		const isLastRequiredAgreement =
			unsignedRequiredAgreements.length === 1 && unsignedRequiredAgreements[0].id === agreement.id;

		await handleAgree(agreement, isLastRequiredAgreement);
	};

	return (
		<>
			<StyledDivider orientation="horizontal" flexItem />

			<Grid item xs={12} px={2}>
				<Typography variant={'body1'} mb={4}>
					<b>{CUSTODIAN_AGREEMENTS_TEXTS.sectionTitle}</b>
				</Typography>

				{unsignedRequiredAgreements.length > 0 ? (
					<Alert severity="warning" sx={{ mb: 3 }}>
						{CUSTODIAN_AGREEMENTS_TEXTS.requiredNotSigned}
					</Alert>
				) : null}

				<Grid container rowSpacing={2}>
					{agreements.map(agreement => {
						const mandatority = getAgreementMandatorityLabel(agreement);

						return (
							<Grid item xs={12} key={agreement.id}>
								<Stack alignItems={'center'} justifyContent={'space-between'} direction={'row'}>
									<Stack direction={'column'}>
										<Typography variant={'caption'}>
											<b>{agreement.name}</b>
										</Typography>

										{agreement.agreedAt ? (
											<Typography variant={'caption'} color={'textSecondary'}>
												Agreed at {moment(agreement.agreedAt).format('MMM Do, YYYY [at] h:mm A')}
											</Typography>
										) : null}
									</Stack>

									<Grid item display={'flex'} alignItems={'center'}>
										{getAgreementStatusIcon(agreement)}

										<Stack direction={'column'} ml={1.5} mr={2}>
											<Typography variant={'caption'}>{getAgreementStatusLabel(agreement)}</Typography>

											{mandatority ? (
												<Typography variant={'caption'} color={'textSecondary'}>
													{`(${mandatority})`}
												</Typography>
											) : null}
										</Stack>

										<Divider orientation="vertical" flexItem />

										<Box ml={1}>
											<Tooltip title="View agreement" placement="top">
												<IconButton onClick={() => handleOpenAgreement(agreement)} aria-label="View agreement">
													<VisibilityIcon />
												</IconButton>
											</Tooltip>
										</Box>
									</Grid>
								</Stack>
							</Grid>
						);
					})}
				</Grid>
			</Grid>

			<CustodianAgreementModal
				isOpen={Boolean(selectedFromStore)}
				onClose={() => setSelectedAgreement(null)}
				agreements={agreements}
				selectedAgreement={selectedFromStore}
				onSelect={handleOpenAgreement}
				onAgree={handleAgreeAgreement}
				agreeingId={agreeingId}
			/>
		</>
	);
}

export default CustodianAgreements;
